// User Mention Web Component
// Displays a username with friend request button/badge based on relationship
import { escapeHtml } from '../utils/html-utils.js';

class UserMention extends HTMLElement {
  // Static cache for relationship data
  static cache = new Map();

  // Static map to track in-flight requests (deduplication)
  static inFlightRequests = new Map();

  // Cache TTL: 5 minutes
  static CACHE_TTL = 5 * 60 * 1000;

  // Flag to track if event listeners are set up
  static eventListenersSetup = false;

  constructor() {
    super();
    this.userId = null;
    this.mentionedUserId = null;
    this.username = null;
    this.realUsername = null;
    this.relationship = null;
    this.isLoading = false;

    // Set up static event listeners once
    if (!UserMention.eventListenersSetup) {
      UserMention.setupStaticEventListeners();
      UserMention.eventListenersSetup = true;
    }
  }

  /**
   * Set up static event listeners for cache invalidation
   */
  static setupStaticEventListeners() {
    // Listen for friend request sent
    document.addEventListener('friend-request-sent', (e) => {
      const { userId: mentionedUserId } = e.detail || {};
      if (mentionedUserId) {
        // Invalidate cache for the mentioned user
        // This will invalidate all relationships involving this user
        UserMention.invalidateUserRelationship(mentionedUserId);
      }
    });

    // Listen for friend request accepted
    document.addEventListener('friend-request-accepted', (e) => {
      const { friendship } = e.detail || {};
      if (friendship) {
        // Invalidate cache for both users in the friendship
        if (friendship.userId) {
          UserMention.invalidateUserRelationship(friendship.userId);
        }
        if (friendship.friendId) {
          UserMention.invalidateUserRelationship(friendship.friendId);
        }
      } else {
        // If no friendship object, invalidate all (safer approach)
        UserMention.clearCache();
      }
    });

    // Listen for friend request declined
    document.addEventListener('friend-request-declined', (_e) => {
      // When declined, we might not have user info in event
      // Invalidate all cache to be safe (relationship status might have changed)
      UserMention.clearCache();
    });

    // Listen for invitation accepted (creates friendship)
    document.addEventListener('invitation-accepted', (e) => {
      const { friendship } = e.detail || {};
      if (friendship) {
        if (friendship.userId) {
          UserMention.invalidateUserRelationship(friendship.userId);
        }
        if (friendship.friendId) {
          UserMention.invalidateUserRelationship(friendship.friendId);
        }
      } else {
        UserMention.clearCache();
      }
    });
  }

  /**
   * Generate cache key from current user ID and mentioned username/userId
   */
  static getCacheKey(currentUserId, username, mentionedUserId = null) {
    if (mentionedUserId) {
      // Use IDs for more reliable caching
      return `${currentUserId}:${mentionedUserId}`;
    }
    // Fallback to username if ID not available yet
    return `${currentUserId}:${username}`;
  }

  /**
   * Check if cached data is still valid
   */
  static isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    const now = Date.now();
    return (now - cacheEntry.timestamp) < UserMention.CACHE_TTL;
  }

  /**
   * Get cached relationship data
   * Tries multiple cache key formats to find cached data
   */
  static getCachedRelationship(currentUserId, username, mentionedUserId = null) {
    // Try with mentionedUserId first if available
    if (mentionedUserId) {
      const keyWithId = UserMention.getCacheKey(currentUserId, username, mentionedUserId);
      const cacheEntryWithId = UserMention.cache.get(keyWithId);

      if (cacheEntryWithId && UserMention.isCacheValid(cacheEntryWithId)) {
        return cacheEntryWithId.data;
      }

      // Remove expired entry
      if (cacheEntryWithId) {
        UserMention.cache.delete(keyWithId);
      }
    }

    // Try with username (fallback for when mentionedUserId wasn't available when cached)
    const keyWithUsername = UserMention.getCacheKey(currentUserId, username, null);
    const cacheEntryWithUsername = UserMention.cache.get(keyWithUsername);

    if (cacheEntryWithUsername && UserMention.isCacheValid(cacheEntryWithUsername)) {
      // Found a cache entry by username - use it and update cache with ID-based key if we have ID
      const data = cacheEntryWithUsername.data;

      // Use the ID from cached data if available, otherwise use the one passed in
      const finalMentionedUserId = data.mentionedUserId || mentionedUserId;

      // If we have an ID, also cache it with the ID-based key for future lookups
      if (finalMentionedUserId) {
        // Update the data if we're adding an ID that wasn't there before
        if (!data.mentionedUserId && mentionedUserId) {
          data.mentionedUserId = mentionedUserId;
        }
        const keyWithId = UserMention.getCacheKey(currentUserId, username, finalMentionedUserId);
        // Update the cache entry to include the ID
        const updatedEntry = {
          ...cacheEntryWithUsername,
          mentionedUserId: finalMentionedUserId
        };
        UserMention.cache.set(keyWithId, updatedEntry);
      }

      return data;
    }

    // Remove expired entry
    if (cacheEntryWithUsername) {
      UserMention.cache.delete(keyWithUsername);
    }

    return null;
  }

  /**
   * Store relationship data in cache
   * Stores under both ID-based and username-based keys for better cross-component lookups
   */
  static setCachedRelationship(currentUserId, username, mentionedUserId, relationship) {
    const cacheEntry = {
      data: relationship,
      timestamp: Date.now(),
      mentionedUserId: mentionedUserId || relationship.mentionedUserId // Store for easier invalidation
    };

    // Store with ID-based key if we have an ID
    if (mentionedUserId || relationship.mentionedUserId) {
      const idKey = UserMention.getCacheKey(currentUserId, username, mentionedUserId || relationship.mentionedUserId);
      UserMention.cache.set(idKey, cacheEntry);
    }

    // Also always store with username-based key (for components that don't have ID yet)
    const usernameKey = UserMention.getCacheKey(currentUserId, username, null);
    UserMention.cache.set(usernameKey, cacheEntry);
  }

  /**
   * Invalidate cache entries for a specific user relationship
   * This invalidates all cache entries that involve the given userId
   * (either as current user or mentioned user)
   */
  static invalidateUserRelationship(userId) {
    if (!userId) return;

    // Remove all cache entries that involve this user
    // Cache keys are in format: "currentUserId:mentionedUserId" or "currentUserId:username"
    const keysToDelete = [];
    for (const [key, entry] of UserMention.cache.entries()) {
      // Check if entry has this user as mentionedUserId
      if (entry.mentionedUserId === userId) {
        keysToDelete.push(key);
      }
      // Check if key contains this userId (as current or mentioned)
      else if (key.includes(`:${userId}`) || key.startsWith(`${userId}:`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      UserMention.cache.delete(key);
      // Also remove from in-flight requests to prevent stale data
      UserMention.inFlightRequests.delete(key);
    });

    if (keysToDelete.length > 0) {
      window.logger.debug(`Invalidated ${keysToDelete.length} cache entries for user ${userId}`);
    }
  }

  /**
   * Clear all cached relationship data
   */
  static clearCache() {
    UserMention.cache.clear();
  }

  static get observedAttributes() {
    return ['user-id', 'username', 'real-username', 'mentioned-user-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'user-id') {
        this.userId = newValue ? parseInt(newValue, 10) : null;
      } else if (name === 'username') {
        this.username = newValue;
      } else if (name === 'real-username') {
        this.realUsername = newValue;
      } else if (name === 'mentioned-user-id') {
        this.mentionedUserId = newValue;
      }
      this.loadRelationship();
    }
  }

  connectedCallback() {
    // Get initial values from attributes
    // this is the current user
    this.userId = this.getAttribute('user-id') ? parseInt(this.getAttribute('user-id'), 10) : null;

    // this is the username to disply
    this.username = this.getAttribute('username');

    // this is the real username
    this.realUsername = this.getAttribute('real-username');

    this.mentionedUserId = this.getAttribute('mentioned-user-id');

    this.render();
    this.setupEventListeners();

    // Load relationship if we have both userId and currentUser
    if (this.userId && this.currentUser) {
      this.loadRelationship();
    }
  }

  setupEventListeners() {
    // Handle friend request button clicks
    this.addEventListener('click', async (e) => {
      const button = e.target.closest('[data-action="send-friend-request"]');
      if (button) {
        await this.handleSendFriendRequest(button);
      }
    });
  }

  /**
   * Set the current user (called by parent components)
   */
  setCurrentUser(user) {
    this.currentUser = user;
    // Load relationship if we have userId
    if (this.userId) {
      this.loadRelationship();
    }
  }

  async loadRelationship() {
    // Don't load if we don't have currentUser or userId or username
    if (!this.userId || !this.username) {
      return;
    }

    // Determine the username to use
    let username = this.username;
    if (this.realUsername) {
      username = this.realUsername;
    }

    // Check cache first (try with mentionedUserId if we have it, otherwise by username)
    const cachedRelationship = UserMention.getCachedRelationship(
      this.userId,
      username,
      this.mentionedUserId
    );

    if (cachedRelationship) {
      this.relationship = cachedRelationship;
      // If we got from cache and don't have mentionedUserId yet, try to extract it
      if (!this.mentionedUserId && cachedRelationship.mentionedUserId) {
        this.mentionedUserId = cachedRelationship.mentionedUserId;
      }
      // always override this so it links to the right page
      this.realUsername = cachedRelationship.realUsername;
      this.render();
      return;
    }

    // Check if there's an in-flight request for this relationship
    // Try both cache key formats to catch in-flight requests from other components
    const cacheKeyWithId = UserMention.getCacheKey(this.userId, username, this.mentionedUserId);
    const cacheKeyWithUsername = UserMention.getCacheKey(this.userId, username, null);

    // Check for in-flight request by ID first, then by username
    let inFlightRequest = UserMention.inFlightRequests.get(cacheKeyWithId);
    if (!inFlightRequest && cacheKeyWithUsername !== cacheKeyWithId) {
      inFlightRequest = UserMention.inFlightRequests.get(cacheKeyWithUsername);
    }

    if (inFlightRequest) {
      // Wait for the existing request to complete
      // Set loading state while waiting (other component is making the request)
      this.isLoading = true;
      this.render();

      try {
        const relationship = await inFlightRequest;
        this.relationship = relationship;
        if (!this.mentionedUserId && relationship.mentionedUserId) {
          this.mentionedUserId = relationship.mentionedUserId;
        }
        // Clear loading state before rendering
        this.isLoading = false;
        this.render();
      } catch (error) {
        // If the request failed, clear loading and try to show something
        window.logger.error('In-flight request failed:', error);
        this.isLoading = false;
        this.relationship = {
          canSendFriendRequest: false,
          hasPendingRequest: false,
          isFriend: false,
          isSelf: false
        };
        this.render();
      }
      return;
    }

    // Start loading
    this.isLoading = true;
    this.render();

    // Create a promise for this request
    // Store it under both key formats so other components can find it
    const requestPromise = this.fetchRelationship(username);
    UserMention.inFlightRequests.set(cacheKeyWithId, requestPromise);
    if (cacheKeyWithUsername !== cacheKeyWithId) {
      UserMention.inFlightRequests.set(cacheKeyWithUsername, requestPromise);
    }

    try {
      const relationship = await requestPromise;
      this.relationship = relationship;

      // Store in cache
      UserMention.setCachedRelationship(
        this.userId,
        username,
        this.mentionedUserId,
        relationship
      );

      // Set loading to false before rendering
      this.isLoading = false;
      this.render();
    } catch (error) {
      window.logger.error('Failed to load relationship:', error);
      this.relationship = {
        canSendFriendRequest: false,
        hasPendingRequest: false,
        isFriend: false,
        isSelf: false
      };
      // Set loading to false before rendering
      this.isLoading = false;
      this.render();
    } finally {
      // Clean up in-flight requests from both key formats
      const cacheKeyWithId = UserMention.getCacheKey(this.userId, username, this.mentionedUserId);
      const cacheKeyWithUsername = UserMention.getCacheKey(this.userId, username, null);
      UserMention.inFlightRequests.delete(cacheKeyWithId);
      if (cacheKeyWithUsername !== cacheKeyWithId) {
        UserMention.inFlightRequests.delete(cacheKeyWithUsername);
      }
    }
  }

  /**
   * Fetch relationship data from API
   */
  async fetchRelationship(username) {
    const response = await fetch(`/api/users/profile/${encodeURIComponent(username)}`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      const mentionedUserId = data.user.id;

      // Store mentionedUserId if we don't have it
      if (!this.mentionedUserId) {
        this.mentionedUserId = mentionedUserId;
      }
      // always set the canonical username from the server
      this.realUsername = data.user.username;

      // Check if it's self
      if (this.userId === mentionedUserId) {
        const relationship = { isSelf: true, mentionedUserId, realUsername: data.user.username };
        return relationship;
      }

      // Return relationship data with mentionedUserId included
      if (data.relationship) {
        return { ...data.relationship, mentionedUserId, realUsername: data.user.username };
      } else {
        // If no relationship data, assume we can't send a request
        return {
          canSendFriendRequest: false,
          hasPendingRequest: false,
          isFriend: false,
          isSelf: false,
          mentionedUserId
        };
      }
    } else {
      // If profile is not accessible, assume we can't send a request
      return {
        canSendFriendRequest: false,
        hasPendingRequest: false,
        isFriend: false,
        isSelf: false,
        mentionedUserId: null
      };
    }
  }

  async handleSendFriendRequest(button) {
    if (!this.mentionedUserId) {
      window.logger.error('No user ID provided for friend request');
      return;
    }

    try {
      button.disabled = true;
      button.textContent = 'Sending...';

      const response = await fetch('/api/friends/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friendId: Number(this.mentionedUserId)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to send friend request');
      }

      window.logger.info('Friend request sent successfully');

      // Invalidate cache for this relationship
      if (this.mentionedUserId) {
        UserMention.invalidateUserRelationship(this.mentionedUserId);
      }

      // Reload the relationship to update the UI
      await this.loadRelationship();

      // Dispatch event to update friends list if it's visible
      // Note: userId here refers to the mentioned user, not current user
      document.dispatchEvent(new CustomEvent('friend-request-sent', {
        detail: { userId: this.mentionedUserId }
      }));

    } catch (error) {
      window.logger.error('Failed to send friend request:', error);
      button.disabled = false;
      button.innerHTML = '🤝<span class="add-friend-title">Add Friend</span>';
      alert(`Failed to send friend request: ${error.message}`);
    }
  }

  render() {
    if (!this.username) {
      this.innerHTML = '';
      return;
    }
    let username = this.username;
    if (this.realUsername) {
      username = this.realUsername;
    }
    if (this.relationship) {
      username = this.relationship.realUsername;
    }

    const usernameLink = `<a href="/profile/${escapeHtml(username)}" class="user-mention-link">${escapeHtml(this.username)}</a>`;

    // If loading, show username with loading state
    if (this.isLoading) {
      this.innerHTML = `
        <div class="user-mention">
          ${usernameLink}
          <span class="user-mention-loading">⏳</span>
        </div>
      `;
      return;
    }

    // If no relationship data yet, just show username
    if (!this.relationship) {
      this.innerHTML = `
        <div class="user-mention">
          ${usernameLink}
        </div>
      `;
      return;
    }

    // If it's self, just show username
    if (this.relationship.isSelf) {
      this.innerHTML = `
        <div class="user-mention">
          ${usernameLink}
        </div>
      `;
      return;
    }

    // If already friends, just show username
    if (this.relationship.isFriend) {
      this.innerHTML = `
        <div class="user-mention">
          ${usernameLink}
        </div>
      `;
      return;
    }

    // Show username with friend request button or pending badge
    const friendRequestButton = this.relationship.canSendFriendRequest
      ? `<button title="Add Friend" class="btn btn-sm btn-success" data-action="send-friend-request" data-user-id="${this.userId}">🤝<span class="add-friend-title"> Add Friend</span></button>`
      : '';

    const pendingBadge = this.relationship.hasPendingRequest
      ? '<span class="pending-badge">⏳ Pending</span>'
      : '';

    this.innerHTML = `
      <div class="user-mention">
        ${usernameLink}
        ${friendRequestButton}
        ${pendingBadge}
      </div>
    `;
  }
}

// Register the custom element
customElements.define('user-mention', UserMention);


